/**
 * Design System - Tab Component
 * Reusable tab component for navigation
 */

import React from 'react';
import {
  typographyColors,
  backgroundColors,
  spacing,
  borders,
  typography,
  fontPrimary,
} from '../../utils';

export interface TabProps {
  label: string;
  isActive?: boolean;
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  disabled?: boolean;
  className?: string;
}

export const Tab: React.FC<TabProps> = ({
  label,
  isActive = false,
  onClick,
  size = 'md',
  disabled = false,
  className = '',
}) => {
  const sizeConfig = {
    xs: {
      paddingX: spacing[1.5],
      paddingY: spacing[1],
      fontSize: typography.size.xs,
    },
    sm: {
      paddingX: spacing[2],
      paddingY: spacing[1.5],
      fontSize: typography.size.xs,
    },
    md: {
      paddingX: spacing[3],
      paddingY: spacing[2],
      fontSize: typography.size.sm,
    },
    lg: {
      paddingX: spacing[4],
      paddingY: spacing[3],
      fontSize: typography.size.base,
    },
  };

  const config = sizeConfig[size];

  // If tab is active and disabled, keep active styling but prevent clicks
  const shouldShowDisabledStyle = disabled && !isActive;

  return (
    <button
      onClick={
        disabled
          ? undefined
          : e => {
              // Prevent default scroll behavior when clicking tabs
              e.preventDefault();
              onClick?.(e);
            }
      }
      disabled={disabled}
      className={className}
      style={{
        backgroundColor: isActive ? backgroundColors.secondary : 'transparent',
        border: 'none',
        borderRadius: borders.radius.lg,
        paddingLeft: config.paddingX,
        paddingRight: config.paddingX,
        paddingTop: config.paddingY,
        paddingBottom: config.paddingY,
        cursor: disabled ? 'not-allowed' : onClick ? 'pointer' : 'default',
        transition: 'all 0.2s',
        fontFamily: fontPrimary,
        fontSize: typography.size.base,
        fontStyle: 'normal',
        fontWeight: typography.weight.regular,
        lineHeight: 'normal',
        color: shouldShowDisabledStyle
          ? typographyColors.tertiary
          : typographyColors.primary,
        opacity: shouldShowDisabledStyle ? 0.5 : 1,
      }}
      onMouseEnter={e => {
        if (onClick && !isActive && !disabled) {
          e.currentTarget.style.backgroundColor = backgroundColors.secondary;
        }
      }}
      onMouseLeave={e => {
        if (onClick && !isActive && !disabled) {
          e.currentTarget.style.backgroundColor = 'transparent';
        }
      }}
    >
      {label}
    </button>
  );
};
